<template>
  <div v-if="data" :class="[rootClasses, `valign-${data.verticalAlign}`, `valign-mobile-${mobileVerticalAlign}`]">
    <div class="asset">
      <suspense>
        <ContentfulResponsiveAsset v-if="data.asset" v-bind="data.asset">
          <template v-slot:description-override><span></span></template>
        </ContentfulResponsiveAsset>
      </suspense>
    </div>
    <div class="typography" :style="textStyle">
      <div class="inner" :class="[`mw-adjusted-align-${data.textAlign}`, `mw-adjusted-mobile-align-${mobileAlignment}`, headingStyle]">
        <h1 :class="[data.fontWeight, data.fontStyle === 'normalcase' ? 'no-transform' : 'uppercase']">
          {{ data.headline }}
        </h1>
        <h2 class="subheading" :class="[subheadingFontWeight, subheadingFontStyle === 'normalcase' ? 'no-transform' : 'uppercase']">
          {{ data.subheading }}
        </h2>
        <h2 v-if="data.cta">
          <suspense>
            <ContentfulCTAButton v-bind="data.cta" />
          </suspense>
        </h2>
      </div>
    </div>
  </div>
</template>

<script>
import { useContentful, contentfulProps } from '@/compositions/useContentful.js';
import { computed } from 'vue';
import ContentfulResponsiveAsset from './ContentfulResponsiveAsset.vue';
import ContentfulCTAButton from './ContentfulCTAButton.vue';

export default {
  components: {
    ContentfulResponsiveAsset,
    ContentfulCTAButton,
  },
  props: contentfulProps,
  async setup(p, c) {
    const { data, init } = useContentful('hero', p, c);

    const textStyle = computed(() => {
      const styles = [];
      if (data.value.fontColor) styles.push(`color:${data.value.fontColor}`);
      if (data.value.fontShadow) styles.push(`text-shadow: 0 0 20px ${data.value.fontShadow}`);
      return styles.join(';');
    });
    const rootClasses = computed(() => {
      const classes = ['hero'];
      if (data.value.fullWidth) classes.push('bleedout', 'to-edge');
      if (data.value.textBelowImageInMobile) classes.push('text-below-in-mobile');
      return classes;
    });

    const headingStyle = computed(() => {
      const headingClass = [];
      if (data && data.value && data.value.maxWidth) {
        let typographyMaxWidth = data.value.maxWidth.toLowerCase(); // width: Narrow ,Normal , Wide ,100% of parent
        if (typographyMaxWidth === '100% of parent') {
          typographyMaxWidth = 'full';
        }

        if (data.value.fontStyle && typographyMaxWidth) {
          headingClass.push(`ctf-heading-${data.value.fontStyle}-${typographyMaxWidth}`);
        }
      }
      return headingClass;
    });

    const subheadingFontWeight = computed(() => {
      if (data.value?.subheadingFontWeight) {
        return data.value?.subheadingFontWeight;
      } else if (data.value?.fontWeight) {
        return data.value?.fontWeight;
      } else return 'regular';
    });

    const subheadingFontStyle = computed(() => {
      if (data.value?.subheadingFontStyle) {
        return data.value?.subheadingFontStyle;
      } else if (data.value?.fontStyle) {
        return data.value?.fontStyle;
      } else return 'normalcase';
    });

    const mobileAlignment = computed(() => {
      if (data.value?.mobileTextAlign) {
        return data.value?.mobileTextAlign;
      } else if (data.value?.textAlign) {
        return data.value?.textAlign;
      } else return 'center';
    });

    const mobileVerticalAlign = computed(() => {
      if (data.value?.mobileVerticalAlign) {
        return data.value?.mobileVerticalAlign;
      } else if (data.value?.verticalAlign) {
        return data.value?.verticalAlign;
      } else return 'center';
    });

    await init();
    return {
      data,
      textStyle,
      rootClasses,
      headingStyle,
      subheadingFontWeight,
      subheadingFontStyle,
      mobileAlignment,
      mobileVerticalAlign,
    };
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.hero {
  position: relative;
  /* purgecss ignore */
  &.valign-top {
    .typography {
      align-items: flex-start;
    }
  }
  /* purgecss ignore */
  &.valign-center {
    .typography {
      align-items: center;
    }
  }
  /* purgecss ignore */
  &.valign-bottom {
    .typography {
      align-items: flex-end;
    }
  }

  @media (max-width: calc($mobile-breakpoint - 1px)) {
    &.valign-mobile-top {
      .typography {
        align-items: flex-start;
      }
    }
    /* purgecss ignore */
    &.valign-mobile-center {
      .typography {
        align-items: center;
      }
    }
    /* purgecss ignore */
    &.valign-mobile-bottom {
      .typography {
        align-items: flex-end;
      }
    }

    .mw-adjusted-mobile-align-left {
      --mw-align: left;
      --mw-align-flex: start;
      text-align: left;
      /* purgecss ignore */
      & > {
        h1,
        h2,
        .align {
          /* purgecss ignore */
          &:not([class^='pxl-h']) {
            margin-right: auto;
          }
        }
      }
    }
    /* purgecss ignore */
    .mw-adjusted-mobile-align-center {
      --mw-align: center;
      --mw-align-flex: center;
      text-align: center;
      /* purgecss ignore */
      & > {
        h1,
        h2,
        .align {
          /* purgecss ignore */
          &:not([class^='pxl-h']) {
            margin-right: auto;
            margin-left: auto;
          }
        }
      }
    }
    /* purgecss ignore */
    .mw-adjusted-mobile-align-right {
      --mw-align: right;
      --mw-align-flex: end;
      text-align: right;
      /* purgecss ignore */
      & > {
        h1,
        h2,
        .align {
          /* purgecss ignore */
          &:not([class^='pxl-h']) {
            margin-left: auto;
          }
        }
      }
    }

    &.text-below-in-mobile {
      .typography {
        position: static;
        text-align: left;
        color: revert !important;
        text-shadow: revert !important;
      }

      .button-text a.filled.white {
        border-color: var(--grey-dark);
        color: var(--grey-dark) !important;
      }
    }
  }

  .asset {
    .description {
      display: none;
    }
  }

  .typography {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    pointer-events: none;

    a,
    button {
      pointer-events: all;
    }

    .inner {
      width: 100%;
    }

    h1,
    h2 {
      color: inherit;
    }
    h1 {
      line-height: 100%;
      max-width: 27em;
    }
    h2 {
      line-height: 125%;
      max-width: 40.5em;
    }

    .ctf-heading-normalcase-narrow {
      h1,
      h2 {
        max-width: 20em;
      }
    }

    .ctf-heading-normalcase-normal {
      h1 {
        max-width: 20em;
      }

      h2 {
        max-width: 30em;
      }
    }

    .ctf-heading-normalcase-wide {
      h1 {
        max-width: 24em;
      }
      h2 {
        max-width: 34em;
      }
    }
    .ctf-heading-uppercase-narrow {
      h1 {
        max-width: 20em;
      }
      h2 {
        max-width: calc(20em * 1.5);
      }
    }

    .ctf-heading-uppercase-normal {
      h1 {
        max-width: 20em;
      }

      h2 {
        max-width: calc(30em * 1.5);
      }
    }

    .ctf-heading-uppercase-wide {
      h1 {
        max-width: 24em;
      }
      h2 {
        max-width: calc(34em * 1.5);
      }
    }
    .ctf-heading-uppercase-full,
    .ctf-heading-normalcase-full {
      h1,
      h2 {
        max-width: 100%;
      }
    }
  }

  .typography {
    @mixin responsiveContainerPadding;

    h2:last-of-type {
      margin-bottom: 0px !important;
    }

    h1 {
      font-size: var(--font-size-32);
      margin-bottom: 8px;
    }
    h2 {
      font-size: var(--font-size-16);
      margin-bottom: 8px;
    }
    padding-top: 16px;
    padding-bottom: 16px;
  }

  @media (min-width: $mobile-breakpoint) {
    .typography {
      h1 {
        font-size: var(--font-size-32);
      }
      h2 {
        font-size: var(--font-size-16);
      }
      padding-top: var(--space-6);
      padding-bottom: var(--space-6);
    }
  }

  /* &.bleedout { */
  @media (min-width: $desktop-breakpoint) {
    .typography {
      h1 {
        font-size: 56px;
        margin-bottom: 34px;
        line-height: 34px;
      }
      h2 {
        font-size: 20px;
        margin-bottom: 16px;

        &.subheading {
          margin-bottom: 34px;
        }
      }
      padding-top: 40px;
      padding-bottom: 40px;
    }
  }

  @media (min-width: $large-breakpoint) {
    .typography {
      h1 {
        font-size: 64px;
        margin-bottom: 34px;
        line-height: 40px;
      }
      h2 {
        margin-bottom: 16px;
        font-size: 20px;

        &.subheading {
          font-size: 24px;
          margin-bottom: 34px;
        }
      }
      padding-top: 64px;
      padding-bottom: 64px;
    }
  }

  @media (min-width: $xxl-breakpoint) {
    .typography {
      h1 {
        font-size: 96px;
        margin-bottom: 34px;
        line-height: 62px;
      }
      h2 {
        font-size: 20px;

        &.subheading {
          font-size: 32px;
          margin-bottom: 34px;
        }
      }
      padding-top: 56px;
      padding-bottom: 56px;
    }
  }
  /* } */
}
</style>
